import React from "react";

import { formatNumber, isFieldEnrolled, useGetFieldDetails, useTranslation } from "utils";

import { CROP_TYPE_FCPO, CarbonProgram, FieldListField, PracticeBundle } from "models";

import { Button, FieldParticipationTags, Flex, Icon, NoPayoutTag, Tag, Typography } from "components";

import { LazySoilPlus3Menu } from "pages/app/all-fields-data-table-view/components/LazySoilPlus3Menu";
import { FieldMap } from "pages/app/historical-practice-data-table-view/components/FieldMap";
import { PracticeTableTooltip } from "pages/app/historical-practice-data-table-view/components/PracticeTableTooltip";

// same field cell Timeline shows (thumbnail, enrolled tag, crop-type tag, 3-dot SoilPlus3Menu) but without
// Timeline's payout/open-tasks/SoilSight wiring, which the Table view has no equivalent for (PE-821)
export const TableFieldCell = ({
  field,
  carbonProgram,
  availablePracticeBundles,
  hasNoPayoutDueToNoPoints = false,
  cropType,
  year,
  activeFarmReportingYear,
  isSubRow,
  isFirstCropForField,
  isLastCropForField,
  onAddCrop,
  addCropDisabledReason,
  hasAnyRowSelected,
  hideHoverAddCropButton,
  onEditField,
  onDeleteField,
}: {
  field: FieldListField;
  carbonProgram: CarbonProgram | null;
  availablePracticeBundles: PracticeBundle[];
  // the field's crops or organic fertilisers aren't covered by the program's SOC model yet, so it earns no payout
  hasNoPayoutDueToNoPoints?: boolean;
  cropType?: CROP_TYPE_FCPO;
  // the selected harvest year, to check whether it falls within this field's baseline years
  year?: number;
  // the farm's current reporting year (not the selected year), which decides whether the reactivated tag shows
  activeFarmReportingYear: number | undefined;
  // true for a field's 2nd+ crop this year (e.g. a Cover crop alongside its Main crop) — skips the
  // thumbnail/name/enrolled tag already shown on the field's first row, showing just this crop's type instead
  isSubRow?: boolean;
  isFirstCropForField?: boolean;
  // true when this is the field's last crop row — the connector's trunk line stops at this row's elbow
  // instead of continuing down to link with another sub-row below
  isLastCropForField?: boolean;
  onAddCrop?: () => void;
  // when set, "Add crop" shows disabled with this text instead of being clickable (e.g. the selected year is
  // outside the range crops can actually be added for)
  addCropDisabledReason?: string;
  // hides the hover "+ Add crop" button while any row is checked, so it doesn't compete with bulk selection
  hasAnyRowSelected?: boolean;
  // placeholder rows already have their own "+ Add crop" button in the Crops column
  hideHoverAddCropButton?: boolean;
  // the drawer and modal live once in the table, not in every row
  onEditField: (field: FieldListField) => void;
  onDeleteField: (field: FieldListField) => void;
}) => {
  const { t } = useTranslation(["allFieldsDataTableView", "farm"]);
  const { data: fieldDetails } = useGetFieldDetails({ id: field.id });

  const isEnrolled = isFieldEnrolled(availablePracticeBundles, carbonProgram);
  const isBaseline = isEnrolled && year !== undefined && !!field.baselineYears?.includes(year);
  const fieldHasActivePractices = availablePracticeBundles.some((bundle) => bundle.active);
  const hectareLimitWouldBeExceeded = !fieldDetails || (fieldDetails.exceedsHectareLimit && !fieldHasActivePractices);
  const isPracticeEnrollmentDisabled = hectareLimitWouldBeExceeded || !carbonProgram;

  const cropTypeTag = cropType && (
    <Tag
      className="chip-pill capitalize"
      colour="grey"
      text={
        <Flex align="center" gap="0.25rem">
          <Icon iconName="klim/crops-matured" size="xs" />
          {t(`allFieldsDataTableView:table.data.cropType.${cropType.toLowerCase()}`)}
        </Flex>
      }
    />
  );

  const fieldTags = (
    <Flex align="center" gap="0.375rem" className="table-field-cell-tags">
      {cropTypeTag}
      <FieldParticipationTags
        className="chip-pill capitalize"
        availablePracticeBundles={availablePracticeBundles}
        carbonProgram={carbonProgram}
        activeFarmReportingYear={activeFarmReportingYear}
      />
      {field.isInRedzone && <Tag className="chip-pill capitalize" colour="red" text={t("farm:redzone")} />}
      {hasNoPayoutDueToNoPoints && <NoPayoutTag className="chip-pill capitalize" />}
      {isBaseline && <Tag className="baseline-chip chip-pill capitalize" colour="blue" text={t("allFieldsDataTableView:table.header.baseline")} />}
    </Flex>
  );

  const addCropButton = onAddCrop && (
    <Button
      type="text"
      size="small"
      className="table-field-cell-hover-add-crop-button"
      onClick={(e) => {
        e.stopPropagation();
        onAddCrop();
      }}
    >
      {t("allFieldsDataTableView:placeholders.addMainCrop")}
    </Button>
  );

  return (
    <>
      <Flex vertical className="table-field-cell-hover-area">
        <Flex justify="space-between" align="flex-start" gap="0.5rem" className="table-field-cell">
          {isSubRow ? (
            <Flex justify="flex-start" align="center" gap="0.75rem" className="table-field-cell-sub-row">
              <div
                className={`table-field-cell-connector ${isFirstCropForField ? "table-field-cell-connector-first" : ""} ${
                  isLastCropForField ? "table-field-cell-connector-last" : ""
                }`}
              />
              {cropTypeTag}
            </Flex>
          ) : (
            <Flex vertical gap="0.5rem" className="table-field-cell-card">
              <Flex justify="flex-start" align="center" gap="0.75rem">
                <div className="table-field-cell-thumbnail">
                  <FieldMap field={field} />
                </div>
                <Flex justify="center" align="flex-start" vertical>
                  <PracticeTableTooltip title={field.name || t("farm:unknownFieldName")}>
                    {({ ref }) => (
                      <div ref={ref} className={`margin-0 historical-practice-data-table-view-field-name ${field.name ? "" : "red-text"}`}>
                        {field.name || t("farm:unknownFieldName")}
                      </div>
                    )}
                  </PracticeTableTooltip>
                  <Flex align="center" gap="0.5rem">
                    <Typography variant="paragraph-s" color="grey-600" className="margin-0">
                      {formatNumber({ number: field.size })} ha
                    </Typography>
                    <Typography variant="paragraph-s" color="grey-600" className="margin-0">
                      {fieldDetails?.number ? t("farm:fieldCellNumber", { number: fieldDetails.number }) : " "}
                    </Typography>
                  </Flex>
                  {fieldTags}
                </Flex>
              </Flex>
            </Flex>
          )}
          {fieldDetails && (
            <LazySoilPlus3Menu
              availablePracticeBundles={availablePracticeBundles}
              carbonProgram={carbonProgram}
              field={fieldDetails}
              hectareLimitWouldBeExceeded={hectareLimitWouldBeExceeded}
              isEnrolled={isEnrolled}
              isPracticeEnrollmentDisabled={isPracticeEnrollmentDisabled}
              placement="bottom-left"
              setShowFieldEditDrawer={() => onEditField(field)}
              setShowDeleteFieldModal={() => onDeleteField(field)}
              onAddCrop={onAddCrop}
              addCropDisabledReason={addCropDisabledReason}
            />
          )}
        </Flex>
        {!isSubRow && onAddCrop && !addCropDisabledReason && !hasAnyRowSelected && !hideHoverAddCropButton && (
          <div className="table-field-cell-add-crop-accordion">
            <div className="table-field-cell-add-crop-accordion-inner">{addCropButton}</div>
          </div>
        )}
      </Flex>
    </>
  );
};
